import React, { useMemo, useState } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Button,
  Typography,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ResponsiveModal from './ResponsiveModal';
import {
  ICON_REGISTRY,
  findIconByName,
  type CategoryIconComponent,
} from '../../utils/categoryIconRegistry';

interface IconPickerProps {
  value?: string | null;
  onChange: (iconName: string) => void;
  label?: string;
}

const IconPicker: React.FC<IconPickerProps> = ({ value, onChange, label = 'آیکون' }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ICON_REGISTRY;
    return ICON_REGISTRY.filter((entry) => {
      if (entry.name.toLowerCase().includes(q)) return true;
      return entry.keywords.some((k) => k.toLowerCase().includes(q));
    });
  }, [query]);

  const SelectedIcon = findIconByName(value || undefined);

  const handlePick = (name: string) => {
    onChange(name);
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
  };

  return (
    <>
      <Box>
        <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted, #64748b)', mb: 0.5 }}>
          {label}
        </Typography>
        <Box
          onClick={() => setOpen(true)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.25,
            border: '1px solid #d1d5db',
            borderRadius: 1,
            cursor: 'pointer',
            bgcolor: 'white',
            transition: 'border-color 0.15s ease',
            '&:hover': { borderColor: '#B25373' },
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 1,
              bgcolor: '#f1f5f9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: SelectedIcon ? '#B25373' : '#94a3b8',
              flexShrink: 0,
            }}
          >
            {SelectedIcon ? (
              <SelectedIcon size={22} />
            ) : (
              <Typography sx={{ fontSize: 12 }}>?</Typography>
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
              {value || 'بدون آیکون - برای انتخاب کلیک کنید'}
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
              از مجموعه‌ای {ICON_REGISTRY.length}‌تایی انتخاب کنید
            </Typography>
          </Box>
          {value && (
            <Tooltip title="حذف آیکون">
              <IconButton size="small" onClick={handleClear}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <ResponsiveModal
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        title="انتخاب آیکون"
        actions={<Button onClick={() => setOpen(false)}>بستن</Button>}
      >
          <TextField
            fullWidth
            autoFocus
            placeholder="جستجو... (مثلاً: پوست، عطر، تخفیف، lipstick)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: query ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setQuery('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ mb: 2 }}
          />

          {filtered.length === 0 ? (
            <Typography sx={{ textAlign: 'center', color: '#94a3b8', py: 4 }}>
              آیکونی پیدا نشد
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(86px, 1fr))',
                gap: 1,
                maxHeight: 480,
                overflowY: 'auto',
                pr: 0.5,
              }}
            >
              {filtered.map((entry) => {
                const Comp = entry.component as unknown as CategoryIconComponent;
                const isSelected = entry.name === value;
                return (
                  <Tooltip key={entry.name} title={entry.name} arrow>
                    <Box
                      onClick={() => handlePick(entry.name)}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.5,
                        p: 1.25,
                        border: `1.5px solid ${isSelected ? '#B25373' : 'transparent'}`,
                        borderRadius: 1.5,
                        bgcolor: isSelected ? '#FFF4F7' : '#f8fafc',
                        color: isSelected ? '#B25373' : '#475569',
                        cursor: 'pointer',
                        transition: 'all 0.12s ease',
                        '&:hover': {
                          bgcolor: '#FFF4F7',
                          color: '#B25373',
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      <Comp size={26} />
                      <Typography
                        sx={{
                          fontSize: 10,
                          fontWeight: isSelected ? 600 : 400,
                          textAlign: 'center',
                          width: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {entry.name}
                      </Typography>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          )}

          <Typography sx={{ mt: 2, fontSize: 11, color: '#94a3b8' }}>
            {filtered.length} از {ICON_REGISTRY.length} آیکون
          </Typography>
      </ResponsiveModal>
    </>
  );
};

export default IconPicker;
